import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem } from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';
import 'devextreme-react/text-area';

import { formatMessage } from 'devextreme/localization';

import ActionType from '../../classes/consts/ActionType';

import DataSourceErrorForm from './DataSourceErrorForm';

import LabelTemplate from '../label-template/LabelTemplate';

import DataSourceApi from "../../api/DataSourceApi";
import CustomerAgentApi from '../../api/CustomerAgentApi';

import DataSourceCrudLogic from '../../logics/datasourcemodule/DataSourceCrudLogic';

import DataSourceTypeListDto from '../../classes/dtos/datasourcemodule/DataSourceTypeListDto';
import CustomerAgentListDto from '../../classes/dtos/customeragentmodule/CustomerAgentListDto';
import DataSourceAuthenticationDto from '../../classes/dtos/datasourcemodule/DataSourceAuthenticationDto';
import DataSourceAuthenticationDetailDto from '../../classes/dtos/datasourcemodule/DataSourceAuthenticationDetailDto';
import UserDto from '../../classes/dtos/accountmodule/UserDto';

import DataSourceFormDto from '../../classes/dtos/datasourcemodule/DataSourceFormDto';


interface DataSourceFormProps {
    Direction: string;
    DataSourceAdded(id: number): void;
}

export interface DataSourceFormHandle {
    Open: (dataSourceId: number | null) => void;
}

const DataSourceForm = forwardRef<DataSourceFormHandle, DataSourceFormProps>(({ Direction, DataSourceAdded }, ref) => {

    const position = { of: '#root' };

    const formRef = useRef<FormRef>(null);

    const [currentDataSourceId, setCurrentDataSourceId] = useState<number | null>(null);

    const [dataSourceTypes, setDataSourceTypes] = useState<DataSourceTypeListDto[]>([]);
    const [customerAgents, setCustomerAgents] = useState<CustomerAgentListDto[]>([]);
    const [withCustomerAgent, setWithCustomerAgent] = useState<boolean>(false);
    const [withCustomerAgentAuthorized, setWithCustomerAgentAuthorized] = useState<boolean>(false);
    const [withInternalAgentAuthorized, setWithInternalAgentAuthorized] = useState<boolean>(false);
    const [withDataSourceAuthentication, setWithDataSourceAuthentication] = useState<boolean>(false);
    const [allAuthentications, setAllAuthentications] = useState<DataSourceAuthenticationDto[]>([]);
    const [currentAuthenticationDetails, setCurrentAuthenticationDetails] = useState<DataSourceAuthenticationDetailDto[]>([]);

    const [internalAgent] = useState<CustomerAgentListDto>(new CustomerAgentListDto(0, "Internal", 3, new UserDto(0, "", "", ""), 1, ""));

    const [dataSourceCrudLogic] = useState<DataSourceCrudLogic>(new DataSourceCrudLogic());

    const [dataSourceErrorIsOpen, setDataSourceErrorIsOpen] = useState<boolean>(false);

    const [dataSourceFormDto, setDataSourceFormDto] = useState<DataSourceFormDto>(new DataSourceFormDto(null, null, null, null));

    const [isOpen, setIsOpen] = useState(false);

    const [action, setAction] = useState<string>("");

    const [saveInProgress, setSaveInProgress] = React.useState(false);

    const [passwordVisibility, setPasswordVisibility] = useState<{ [key: string]: boolean }>({});

    useImperativeHandle(ref, () => ({
        Open(dataSourceId: number | null) {

            if (dataSourceId === null) {
                setAction(ActionType.Create);
            } else {
                setAction(ActionType.Update);
            }

            setCurrentDataSourceId(dataSourceId);

            loadAsync(dataSourceId).then(() => {
                setIsOpen(true);
                if (dataSourceId === null) {
                    setDataSourceFormDto(new DataSourceFormDto(null, formatMessage("DataSourceForm_DefaultName"), null, null));
                }
            });
        }
    }));

    const loadAsync = async (dataSourceId: number | null): Promise<void> => {
        let readable = (Direction === "Source") ? true : null;
        let writable = (Direction === "Target") ? true : null;

        let getDataSourceTypesResult = await DataSourceApi.GetDataSourceTypesByCustomerAsync(readable, writable);
        if (getDataSourceTypesResult.IsFailed()) {
            alert("Error in GetDataSourceTypesByCustomerAsync");
        }
        setDataSourceTypes(getDataSourceTypesResult.Result.DataSourceTypes);

        let allCustomerAgentsResult = await CustomerAgentApi.GetCustomerAgentsAsync();
        if (allCustomerAgentsResult.IsFailed()) {
            alert("Error in GetCustomerAgentsAsync");
        }
        setCustomerAgents(allCustomerAgentsResult.Result.CustomerAgents);

        if (dataSourceId !== null) {
            let getDataSourceResult = await DataSourceApi.GetDataSourceByIdAsync(dataSourceId);
            if (getDataSourceResult.IsFailed()) {
                alert("Error in GetDataSourceByIdAsync");
            }

            let dataSourceType = getDataSourceTypesResult.Result?.DataSourceTypes.find((e) => e.Id === getDataSourceResult.Result.Type.Id) ?? null;
            let dataSourceAuthentication = dataSourceType?.Authentications.find((e) => e.Key === getDataSourceResult.Result.Parameters.Type) ?? null;
            let customerAgent = allCustomerAgentsResult.Result?.CustomerAgents.find((e) => e.Id === getDataSourceResult.Result.CustomerAgentId) ?? null;

            let withCustomerAgentAuthorized = dataSourceType?.ConnectorTypes.some(e => e.Id === 3) ?? false;
            let withInternalAgentAuthorized = dataSourceType?.ConnectorTypes.some(e => e.Id === 2) ?? false;

            if (customerAgent === null && withInternalAgentAuthorized) {
                customerAgent = internalAgent;
            }

            setWithCustomerAgentAuthorized(withCustomerAgentAuthorized);
            setWithInternalAgentAuthorized(withInternalAgentAuthorized);

            setWithCustomerAgent(withCustomerAgentAuthorized);
            setWithDataSourceAuthentication((dataSourceType?.Authentications.length ?? 0) > 1);

            if (dataSourceType !== null) {
                setAllAuthentications(dataSourceType.Authentications);
            }

            if (dataSourceAuthentication !== null) {
                setCurrentAuthenticationDetails(dataSourceAuthentication?.Details ?? []);
            }

            let data: any = new DataSourceFormDto(dataSourceType, getDataSourceResult.Result.Name, customerAgent, dataSourceAuthentication);

            let parameters: any = getDataSourceResult.Result.Parameters;

            dataSourceAuthentication?.Details.forEach((e) => {
                data[e.Name] = parameters[e.Name];
            });

            setDataSourceFormDto(data);

            forceUpdate();
        }
    }

    const dataSourceTypeEditorOptions = {
        items: dataSourceTypes,
        searchEnabled: true,
        displayExpr: "Name",
        readOnly: action === ActionType.Update,
        onValueChanged: (e: any) => {

            if (!e.event) return;

            let dataSourceType = e.value as DataSourceTypeListDto;

            let withCustomerAgentAuthorized = dataSourceType?.ConnectorTypes.some(e => e.Id === 3) ?? false;
            let withInternalAgentAuthorized = dataSourceType?.ConnectorTypes.some(e => e.Id === 2) ?? false;

            let customerAgent = null;

            if (customerAgent === null && withInternalAgentAuthorized) {
                customerAgent = internalAgent;
            }

            setWithCustomerAgentAuthorized(withCustomerAgentAuthorized);
            setWithInternalAgentAuthorized(withInternalAgentAuthorized);

            setWithCustomerAgent(withCustomerAgentAuthorized);
            setWithDataSourceAuthentication((dataSourceType?.Authentications.length ?? 0) > 1);

            if (dataSourceType === null) {
                setAllAuthentications([]);
                setCurrentAuthenticationDetails([]);
                forceUpdate();
            } else {
                setAllAuthentications(dataSourceType.Authentications);
                let defaultAuthentication = dataSourceType.Authentications[0];
                setCurrentAuthenticationDetails(defaultAuthentication.Details);
                setDataSourceFormDto({
                    ...dataSourceFormDto,
                    CustomerAgent: customerAgent,
                    DataSourceAuthentication: defaultAuthentication
                });
                forceUpdate();
            }
        }
    };

    const dataSourceNameEditorOptions = {
        valueChangeEvent: 'keyup',
        required: true
    };

    const dataSourceAuthenticationEditorOptions = {
        items: allAuthentications,
        searchEnabled: true,
        displayExpr: "Label",
        readOnly: action === ActionType.Update,
        onValueChanged: (e: any) => {
            let dataSourceAuthentication = e.value as DataSourceAuthenticationDto;
            if (dataSourceAuthentication === null) {
                setCurrentAuthenticationDetails([]);
            } else {
                setCurrentAuthenticationDetails(dataSourceAuthentication.Details);
            }

            forceUpdate();
        }
    };

    const GetCustomerAgents = () => {
        if (withInternalAgentAuthorized) {
            return [internalAgent, ...customerAgents];
        } else {
            return customerAgents;
        }
    }

    const customerAgentEditorOptions = {
        items: GetCustomerAgents(),
        searchEnabled: true,
        displayExpr: "Name",
        onValueChanged: (e: any) => {
        }
    };

    const updateValues = function (name: string, value: string) {
    }

    const dataSourceCredentialRequiredEditorOptions = {
        valueChangeEvent: 'keyup',
        required: true,
        onValueChanged: (e: any) => {
            const dataField = e.component.option("name");
            updateValues(dataField, e.value.toString());
        }
    }

    const dataSourceCredentialNotRequiredEditorOptions = {
        valueChangeEvent: 'keyup',
        required: false,
        onValueChanged: (e: any) => {
            const dataField = e.component.option("name");
            updateValues(dataField, e.value.toString());
        }
    }

    const validationRules: {
        dataSourceType: ValidationRule[],
        dataSourceName: ValidationRule[],
        customerAgent: ValidationRule[],
        dataSourceAuthentication: ValidationRule[],
        connectionString: ValidationRule[],
        dataSourceCredential: ValidationRule[]
    } = {
        dataSourceType: [
            { type: 'required', message: formatMessage("DataSourceForm_TypeRequired") },
        ],
        dataSourceName: [
            { type: 'required', message: formatMessage("DataSourceForm_NameRequired") },
        ],
        customerAgent: [
            { type: 'required', message: 'Customer agent is required.' },
        ],
        dataSourceAuthentication: [
            { type: 'required', message: 'Connection type is required.' },
        ],
        connectionString: [
            { type: 'required', message: 'Connection string is required.' },
        ],
        dataSourceCredential: [
            { type: 'required', message: 'Parameters is required.' },
        ]
    };

    const closeDialog = () => {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        };
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        setSaveInProgress(true);

        if (dataSourceFormDto.DataSourceType === null) {
            alert("DataSourceType not set");
            setSaveInProgress(false);
            return;
        }

        if (dataSourceFormDto.DataSourceName === null) {
            alert("DataSourceName not set");
            setSaveInProgress(false);
            return;
        }

        if (withCustomerAgent) {
            if (dataSourceFormDto.CustomerAgent === null) {
                alert("CustomerAgent not set");
                setSaveInProgress(false);
                return;
            }
        }

        if (dataSourceFormDto.DataSourceAuthentication === null) {
            alert("DataSourceAuthentication not set");
            setSaveInProgress(false);
            return;
        }

        let connectorType: number;

        if (withCustomerAgent) {
            connectorType = 3;
        }
        else {
            connectorType = 2;
        }

        let data: any = dataSourceFormDto;

        const mapParameters = new Map<string, string>()
        mapParameters.set("Type", dataSourceFormDto.DataSourceAuthentication.Key);
        currentAuthenticationDetails.forEach((parameter) => {
            mapParameters.set(parameter.Name, data[parameter.Name]);
        })

        let parameters = Object.fromEntries(mapParameters);

        let customerAgentId: number | null = null;
        if ((dataSourceFormDto.CustomerAgent?.Id ?? 0) > 0) {
            customerAgentId = dataSourceFormDto.CustomerAgent?.Id ?? null;
        }

        if (customerAgentId === null) {
            connectorType = 2;
        }

        let createOrUpdateDataSourceResult = await dataSourceCrudLogic.CreateOrUpdateDataSourceAsync(currentDataSourceId, dataSourceFormDto.DataSourceName, dataSourceFormDto.DataSourceType.Id, customerAgentId, connectorType, parameters);

        if (createOrUpdateDataSourceResult.IsFailed()) {
            alert(createOrUpdateDataSourceResult.ErrorMessage);
            setSaveInProgress(false);
            return;
        }

        if (!createOrUpdateDataSourceResult.Result.Ok) {
            setDataSourceErrorIsOpen((previous) => true);
            setSaveInProgress(false);
            return;
        }

        DataSourceAdded(createOrUpdateDataSourceResult.Result.Id);
        setIsOpen(false);
        setSaveInProgress(false);
    };

    const close = () => {
        setIsOpen(false);
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: close
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    function getTitle() {
        switch (action) {
            case ActionType.Create: return formatMessage("DataSourceForm_TitleCreate");
            case ActionType.Update: return formatMessage("DataSourceForm_TitleUpdate");
            default: return "???";
        }
    }

    const [updateTrigger, setUpdateTrigger] = useState(0);

    const forceUpdate = () => setUpdateTrigger(updateTrigger + 1);

    const renderCredentialField = (e: any, index: number) => {
        const isSecret = e.IsSecret;
        const isRequired = e.IsRequired;
        const editorOptionsBase = isRequired
            ? dataSourceCredentialRequiredEditorOptions
            : dataSourceCredentialNotRequiredEditorOptions;

        const editorOptions = {
            ...editorOptionsBase,
            mode: isSecret ? (passwordVisibility[e.Name] ? 'text' : 'password') : 'text',
            ...(isSecret && {
                buttons: [{
                    name: 'password',
                    location: 'after',
                    options: {
                        icon: passwordVisibility[e.Name] ? 'eyeclose' : 'eyeopen',
                        stylingMode: 'text',
                        onClick: () => {
                            setPasswordVisibility(prev => ({
                                ...prev,
                                [e.Name]: !prev[e.Name]
                            }));
                        }
                    }
                }]
            })
        };

        return (
            <SimpleItem
                key={`${index}`}
                dataField={e.Name}
                editorType="dxTextBox"
                editorOptions={editorOptions}
                validationRules={isRequired ? validationRules.dataSourceCredential : undefined}
            >
                <Label text={e.Label} render={LabelTemplate("rename")} />
            </SimpleItem>
        );
    };

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={closeDialog}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={getTitle()}
                container=".dx-viewport"
                width={600}
                height="auto"
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        key={updateTrigger}
                        ref={formRef}
                        formData={dataSourceFormDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="dataSourceDtoData"
                        colCount={2}
                        disabled={saveInProgress}
                    >
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="DataSourceType" editorType="dxSelectBox" editorOptions={dataSourceTypeEditorOptions} validationRules={validationRules.dataSourceType}>
                                <Label text={formatMessage("DataSourceForm_FieldTypeLabel")} render={LabelTemplate('product')} />
                            </SimpleItem>
                            <SimpleItem dataField="DataSourceName" editorOptions={dataSourceNameEditorOptions} validationRules={validationRules.dataSourceName}>
                                <Label text={formatMessage("DataSourceForm_FieldNameLabel")} render={LabelTemplate('rename')} />
                            </SimpleItem>
                            <SimpleItem dataField="DataSourceAuthentication" editorType="dxSelectBox" editorOptions={dataSourceAuthenticationEditorOptions} validationRules={validationRules.dataSourceAuthentication} visible={withDataSourceAuthentication}>
                                <Label text={"Connection type"} render={LabelTemplate('floppy')} />
                            </SimpleItem>
                            <SimpleItem dataField="CustomerAgent" editorType="dxSelectBox" editorOptions={customerAgentEditorOptions} validationRules={validationRules.customerAgent} visible={withCustomerAgent}>
                                <Label text={"Customer agent"} render={LabelTemplate('floppy')} />
                            </SimpleItem>
                            {currentAuthenticationDetails.filter(e => e.ToEnter).map((e, index: number) =>
                                renderCredentialField(e, index)
                            )}
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={saveInProgress}
            />
            <DataSourceErrorForm IsOpen={dataSourceErrorIsOpen} SetIsOpen={setDataSourceErrorIsOpen} ></DataSourceErrorForm>
        </React.Fragment>
    );
});

export default DataSourceForm;